import { replaceUploadPlaceholders } from './build/helpers.js';
import { DI, DW } from './constants.js';
import { curPage, ST } from './state.js';

// ── DOM HELPERS ───────────────────────────────────────────────────

/**
 * @param {string} id
 * @returns {HTMLElement | null}
 */
const $ = id => document.getElementById(id);

/** @param {string} id */
function show(id) {
  $(id)?.classList.remove('gone');
}

/** @param {string} id */
function hide(id) {
  $(id)?.classList.add('gone');
}

/**
 * Create a DOM element with attributes and children.
 * @param {string} tag
 * @param {Record<string, any>} [attrs]
 * @param {...(string | HTMLElement | null)} kids
 * @returns {HTMLElement}
 */
function mk(tag, attrs = {}, ...kids) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') {
      e.className = v;
    } else if (k === 'style') {
      e.style.cssText = v;
    } else if (k.startsWith('on')) {
      e.addEventListener(k.slice(2).toLowerCase(), v);
    } else {
      e.setAttribute(k, v);
    }
  }
  kids.flat().forEach(c => {
    if (c == null) {
      return;
    }
    e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  });
  return e;
}

const dv = (s, ...k) => mk('div', { style: s }, ...k);
const sp = (s, ...k) => mk('span', { style: s }, ...k);

/** @param {string} s @returns {string} */
function esc(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** @param {string} msg */
function showToast(msg) {
  const t = mk(
    'div',
    { class: 'ok-toast' },
    sp('flex:1', msg),
    mk(
      'button',
      {
        style: 'background:none;border:none;color:var(--teal);font-size:16px;cursor:pointer',
        onclick() {
          t.remove();
        },
      },
      '×',
    ),
  );
  document.body.appendChild(t);
  setTimeout(() => {
    if (t) {
      t.remove();
    }
  }, 4000);
}

/** @param {string} msg */
function showErr(msg) {
  document.querySelectorAll('.err-toast').forEach(e => {
    e.remove();
  });
  const t = mk(
    'div',
    { class: 'err-toast' },
    sp('flex:1', `⚠ ${msg}`),
    mk(
      'button',
      {
        style: 'background:none;border:none;color:#b91c1c;font-size:16px;cursor:pointer',
        onclick() {
          t.remove();
        },
      },
      '×',
    ),
  );
  document.body.appendChild(t);
  setTimeout(() => {
    if (t) {
      t.remove();
    }
  }, 7000);
}

// CSS override: force .reveal visible in preview iframes (no scroll-triggered animation)
const REVEAL_FIX =
  '<style data-sf-prev>.reveal{opacity:1!important;transform:translateY(0)!important}</style>';

/**
 * Write full HTML to an iframe via doc.open/write/close.
 * @param {string} id - iframe element ID
 * @param {string} html
 * @param {boolean} [close=true]
 */
function writeFrame(id, html, close) {
  if (close === undefined) {
    close = true;
  }
  const fr = $(id);
  if (!fr) {
    return;
  }
  const doc = fr.contentDocument;
  if (!doc) {
    return;
  }
  // Resolve uploaded-image placeholders to real data URLs
  html = replaceUploadPlaceholders(html);
  // Force .reveal visible in preview — inject override before </head>
  if (html.includes('</head>')) {
    html = html.replace('</head>', `${REVEAL_FIX}</head>`);
  }

  let usedSrcdoc = false;
  try {
    doc.open();
    doc.write(html);
    if (close) {
      doc.close();
    }
  } catch (_e) {
    // doc.write can throw SyntaxError when an unclosed <script> tag causes the
    // HTML parser to consume CSS #id selectors as JS private fields. Fall back to
    // srcdoc which uses a fresh parser and avoids this issue.
    usedSrcdoc = true;
    try {
      fr.srcdoc = html;
    } catch (_e2) {
      /* last resort — ignore */
    }
  }

  // Re-sync inline edit mode after frame rewrite (script re-runs, state resets)
  if (close && ST.inlineEditMode) {
    if (usedSrcdoc) {
      // srcdoc loads async — wait for the iframe to be ready
      fr.addEventListener(
        'load',
        () => {
          fr.contentWindow?.postMessage({ type: 'sf-toggle-edit', enabled: true }, '*');
        },
        { once: true },
      );
    } else {
      setTimeout(() => {
        fr.contentWindow?.postMessage({ type: 'sf-toggle-edit', enabled: true }, '*');
      }, 50);
    }
  }
}

// ── Flicker-free iframe streaming ─────────────────────────────────
// During streaming, patchFrame updates body/head innerHTML in-place
// instead of doc.open/write which causes full re-navigation + flicker.
// First call per session falls back to writeFrame to set up the document.
// finalizeFrame does a clean doc.open/write/close at stream end so scripts run.

/**
 * In-place update of iframe body/head for flicker-free streaming.
 * @param {string} id - iframe element ID
 * @param {string} fullHtml
 */
function patchFrame(id, fullHtml) {
  const fr = $(id);
  if (!fr) {
    return;
  }
  // Resolve uploaded-image placeholders to real data URLs
  fullHtml = replaceUploadPlaceholders(fullHtml);

  try {
    const ifrDoc = fr.contentDocument;
    if (!(ifrDoc?.body && ifrDoc.head)) {
      throw 0;
    }

    // Extract head and body via string slicing — DOMParser moves <style> from body
    // to head, losing section-scoped styles when we read parsed.body.innerHTML
    const headMatch = fullHtml.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
    const bodyMatch = fullHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (!(headMatch && bodyMatch)) {
      throw 0;
    }

    // Update head (styles, meta, fonts) only when changed
    const newHead = headMatch[1];
    if (fr._pHead !== newHead) {
      ifrDoc.head.innerHTML = newHead;
      fr._pHead = newHead;
    }

    // Update body content in-place — no re-navigation, no scroll reset
    ifrDoc.body.innerHTML = bodyMatch[1];

    // Force .reveal visible via CSS override (more reliable than per-element class toggle)
    if (!ifrDoc.head.querySelector('style[data-sf-prev]')) {
      const fix = ifrDoc.createElement('style');
      fix.setAttribute('data-sf-prev', '');
      fix.textContent = '.reveal{opacity:1!important;transform:translateY(0)!important}';
      ifrDoc.head.appendChild(fix);
    }
  } catch (_e) {
    // First call or error: full write to set up the document structure
    writeFrame(id, fullHtml, true);
    fr._pHead = null;
  }
}

/**
 * Clean finalize of iframe after streaming complete.
 * @param {string} id - iframe element ID
 * @param {string} fullHtml
 */
function finalizeFrame(id, fullHtml) {
  const fr = $(id);
  if (fr) {
    fr._pHead = null;
  }
  // writeFrame injects REVEAL_FIX into <head>, so .reveal elements are always visible in preview
  writeFrame(id, fullHtml, true);
}

// ── BUSINESS LOGIC (candidates for extraction to logic/) ────────
// These are pure data functions with no DOM dependency.
// During Vue migration, move to composables.

/** Build plain-text summary of the current brief for AI prompts. @returns {string} */
function briefTxt() {
  const b = ST.brief;
  const lines = [];
  if (b.businessDesc) {
    lines.push(`Business: ${b.businessDesc}`);
  }
  if (b.customerProblem) {
    lines.push(`Customer: ${b.customerProblem}`);
  }
  if (b.desiredAction) {
    lines.push(`Action: ${b.desiredAction}`);
  }
  if (b.actionDetail) {
    lines.push(`Detail: ${b.actionDetail}`);
  }
  if (b.leadMagnet) {
    lines.push(`Lead magnet: ${b.leadMagnet}`);
  }
  if (b.vibes?.length) {
    lines.push(`Personality: ${b.vibes.join(', ')}`);
  }
  if (b.refsite) {
    lines.push(`Reference: ${b.refsite}`);
  }
  const e = ST.enrichment || {};
  if (e.differentiation) {
    lines.push(`Differentiation: ${e.differentiation}`);
  }
  if (e.proof) {
    lines.push(`Proof: ${e.proof}`);
  }
  if (e.objections) {
    lines.push(`Objections: ${e.objections}`);
  }
  if (e.transformation) {
    lines.push(`Transformation: ${e.transformation}`);
  }
  if (e.process) {
    lines.push(`Process: ${e.process}`);
  }
  if (e.localContext) {
    lines.push(`Local: ${e.localContext}`);
  }
  return lines.join('\n\n');
}

/**
 * Load Google Fonts for heading and body.
 * @param {string} h - heading font family
 * @param {string} b - body font family
 */
function loadFont(h, b) {
  let lk = $('sf-gf');
  if (!lk) {
    lk = document.createElement('link');
    lk.id = 'sf-gf';
    lk.rel = 'stylesheet';
    document.head.appendChild(lk);
  }
  lk.href =
    'https://fonts.googleapis.com/css2?family=' +
    encodeURIComponent(h).replace(/%20/g, '+') +
    ':wght@400;600;700;800&family=' +
    encodeURIComponent(b).replace(/%20/g, '+') +
    ':wght@400;500;600&display=swap';
}

// Refresh __notify_email hidden field value in assembled HTML
function refreshNotifyEmail(html) {
  if (!ST.notificationEmail) {
    return html;
  }
  return html.replace(
    /(<input[^>]*name="__notify_email"[^>]*value=")[^"]*(")/gi,
    `$1${esc(ST.notificationEmail)}$2`,
  );
}

/** Assemble full page HTML from shell + sections. @param {boolean} [forceRebuild] @returns {string} */
function assembled(forceRebuild) {
  if (!ST.shellHtml) {
    return '';
  }
  // If edits have been applied, keep the edited version unless force-rebuilding
  if (!forceRebuild && curPage().edited && curPage().assembled) {
    return refreshNotifyEmail(curPage().assembled);
  }
  const sects = ST.sects
    .filter(s => s.status === 'done' && s.html)
    .map(s => s.html)
    .join('\n');
  return refreshNotifyEmail(ST.shellHtml.replace('<!-- SF_SECTIONS -->', sects));
}

/** Assemble HTML for a specific page. @param {import('./types.ts').Page} pg @returns {string} */
function assembledForPage(pg) {
  if (!pg.shellHtml) {
    return '';
  }
  if (pg.edited && pg.assembled) {
    return refreshNotifyEmail(pg.assembled);
  }
  const sects = pg.sects
    .filter(s => s.status === 'done' && s.html)
    .map(s => s.html)
    .join('\n');
  return refreshNotifyEmail(pg.shellHtml.replace('<!-- SF_SECTIONS -->', sects));
}

function renderStepBar(elId, screen) {
  const c = $(elId);
  if (!c) {
    return;
  }
  const steps = ['Design', 'Build'];
  const idx = { brief: 0, followups: 0, summary: 0, concept: 0, design: 0, build: 1 };
  const ci = idx[screen] || 0;
  c.innerHTML = '';
  c.className = 'stepbar';
  steps.forEach((s, i) => {
    const st = i < ci ? 'done' : i === ci ? 'active' : 'idle';
    const item = mk('div', { class: 'step' });
    item.append(
      mk('div', { class: `sdot2 ${st}` }, i < ci ? '✓' : String(i + 1)),
      mk('span', { class: `slbl ${st}` }, s),
    );
    c.append(item);
    if (i < steps.length - 1) {
      c.append(mk('div', { class: `sline ${i < ci ? 'done' : 'idle'}` }));
    }
  });
}

function renderDevSw(elId) {
  const c = $(elId);
  if (!c) {
    return;
  }
  c.innerHTML = '';
  ['desktop', 'tablet', 'mobile'].forEach(deviceType => {
    const b = mk('button', {
      class: `dsw-btn${ST.device === deviceType ? ' active' : ''}`,
      title: deviceType,
      onclick() {
        ST.device = deviceType;
        document.querySelectorAll('.dev-frame').forEach(f => {
          f.style.maxWidth = DW[deviceType];
        });
        document.querySelectorAll('.dev-sw button').forEach((btn, i) => {
          btn.className = `dsw-btn${['desktop', 'tablet', 'mobile'][i] === deviceType ? ' active' : ''}`;
        });
      },
    });
    b.innerHTML = DI[deviceType];
    c.append(b);
  });
}

function mkUpload(label, hint, value, onFile, onClear) {
  if (value) {
    const p = mk('div', { class: 'upprev' });
    const img = mk('img', { class: 'upthumb', src: value.preview || '' });
    const info = dv(
      'flex:1;min-width:0',
      mk(
        'div',
        {
          style:
            'font-size:12px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
        },
        value.name || 'Uploaded',
      ),
      mk('div', { style: 'font-size:10px;color:var(--teal)' }, '✓ AI will analyze this'),
    );
    const clr = mk(
      'button',
      {
        class: 'upclr btn',
        onclick(e) {
          e.stopPropagation();
          onClear();
        },
      },
      '×',
    );
    p.append(img, info, clr);
    return p;
  }
  const z = mk('div', { class: 'upzone' });
  z.append(
    mk('div', { style: 'font-size:18px;color:var(--txt3)' }, '⬆'),
    mk('div', { style: 'font-size:12px;font-weight:500;color:var(--txt2)' }, label),
    mk('div', { style: 'font-size:10px;color:var(--txt3)' }, hint),
  );
  const fi = mk('input', { type: 'file', accept: 'image/*', style: 'display:none' });
  fi.addEventListener('change', e => {
    const f = e.target.files[0];
    if (!f) {
      return;
    }
    const r = new FileReader();
    r.onload = ev => {
      onFile({
        data: ev.target.result.split(',')[1],
        type: f.type,
        name: f.name,
        preview: ev.target.result,
      });
    };
    r.readAsDataURL(f);
  });
  z.addEventListener('click', () => {
    fi.click();
  });
  z.addEventListener('dragover', e => {
    e.preventDefault();
    z.style.borderColor = 'var(--acc)';
  });
  z.addEventListener('dragleave', () => {
    z.style.borderColor = '';
  });
  z.addEventListener('drop', e => {
    e.preventDefault();
    z.style.borderColor = '';
    const f = e.dataTransfer.files[0];
    if (!f?.type.startsWith('image/')) {
      return;
    }
    const r = new FileReader();
    r.onload = ev => {
      onFile({
        data: ev.target.result.split(',')[1],
        type: f.type,
        name: f.name,
        preview: ev.target.result,
      });
    };

    r.readAsDataURL(f);
  });

  z.append(fi);
  return z;
}

function renderLinkPreview(url, containerEl) {
  const placeholder = mk('div', {
    style:
      'width:100%;height:200px;background:var(--s2,#e5e7eb);border-radius:8px;animation:pulse 1.5s ease-in-out infinite',
  });
  containerEl.appendChild(placeholder);

  const img = mk('img', {
    style: 'width:100%;border-radius:8px;display:none',
    src: `/api/screenshot?url=${encodeURIComponent(url)}`,
  });

  img.addEventListener('load', () => {
    placeholder.remove();
    const link = mk('a', { href: url, target: '_blank', rel: 'noopener noreferrer' }, img);
    containerEl.appendChild(link);
    img.style.display = 'block';
  });

  img.addEventListener('error', () => {
    placeholder.remove();
  });
}

function mkLogoInput(onUpdate) {
  const wrap = mk('div', { style: 'display:flex;flex-direction:column;gap:8px' });
  // Track active mode explicitly — deriving from state fails when switching to URL with empty input
  let activeMode = ST.logoUrl?.trim() && !ST.logoImg?.data ? 'url' : 'upload';

  const mkTab = (label, active, onClick) =>
    mk(
      'button',
      {
        class: 'btn',
        style:
          `font-size:11px;padding:4px 12px;border-radius:99px;font-weight:${active ? '600' : '400'};` +
          `background:${active ? 'var(--acc)' : 'var(--s2)'};color:${active ? '#fff' : 'var(--txt2)'};border:none;cursor:pointer`,
        onclick: onClick,
      },
      label,
    );

  const renderContent = mode => {
    // Remove old content (keep tabs)
    while (wrap.children.length > 1) {
      wrap.removeChild(wrap.lastChild);
    }

    if (mode === 'upload') {
      ST.logoUrl = '';
      wrap.append(
        mkUpload(
          'Drop logo or brand image',
          'PNG, JPG · AI extracts your actual colors',
          ST.logoImg,
          img => {
            ST.logoImg = img;
            rebuild();
            onUpdate();
          },
          () => {
            ST.logoImg = null;
            rebuild();
            onUpdate();
          },
        ),
      );
    } else {
      ST.logoImg = null;
      const inp = mk('input', {
        class: 'inp',
        type: 'url',
        placeholder: 'https://example.com/logo.png',
        value: ST.logoUrl || '',
        style: 'font-size:13px',
      });
      inp.addEventListener('input', e => {
        const url = e.target.value.trim();
        ST.logoUrl = url;
        const prev = wrap.querySelector('.logo-url-prev');
        if (prev) {
          prev.remove();
        }
        const hint = wrap.querySelector('.logo-url-hint');
        if (hint) {
          hint.remove();
        }
        if (url && !/^https?:\/\/.+/i.test(url)) {
          wrap.append(
            mk(
              'div',
              { class: 'logo-url-hint', style: 'font-size:10px;color:var(--red,#ef4444)' },
              'Enter a valid URL starting with http:// or https://',
            ),
          );
        } else if (url) {
          const pv = mk('div', {
            class: 'logo-url-prev',
            style:
              'display:flex;align-items:center;gap:8px;padding:6px 8px;background:var(--s2);border-radius:6px',
          });
          const img = mk('img', {
            style:
              'width:32px;height:32px;object-fit:contain;border-radius:4px;flex-shrink:0;background:var(--s1)',
            src: `/api/img-proxy?src=${encodeURIComponent(url)}`,
          });
          img.onerror = () => {
            img.style.display = 'none';
          };
          pv.append(
            img,
            mk(
              'span',
              {
                style:
                  'font-size:10px;color:var(--txt2);overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
              },
              url,
            ),
          );
          wrap.append(pv);
        }
        onUpdate();
      });
      wrap.append(inp);
      if (ST.logoUrl?.trim() && /^https?:\/\/.+/i.test(ST.logoUrl)) {
        const pv = mk('div', {
          class: 'logo-url-prev',
          style:
            'display:flex;align-items:center;gap:8px;padding:6px 8px;background:var(--s2);border-radius:6px',
        });
        const img = mk('img', {
          style:
            'width:32px;height:32px;object-fit:contain;border-radius:4px;flex-shrink:0;background:var(--s1)',
          src: `/api/img-proxy?src=${encodeURIComponent(ST.logoUrl)}`,
        });
        img.onerror = () => {
          img.style.display = 'none';
        };
        pv.append(
          img,
          mk(
            'span',
            {
              style:
                'font-size:10px;color:var(--txt2);overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
            },
            ST.logoUrl,
          ),
        );
        wrap.append(pv);
      }
    }
  };

  const rebuild = () => {
    wrap.innerHTML = '';
    const tabs = mk('div', { style: 'display:flex;gap:4px' });
    tabs.append(
      mkTab('Upload file', activeMode === 'upload', () => {
        activeMode = 'upload';
        rebuild();
        onUpdate();
      }),
      mkTab('Paste URL', activeMode === 'url', () => {
        activeMode = 'url';
        rebuild();
        onUpdate();
      }),
    );
    wrap.append(tabs);
    renderContent(activeMode);
  };

  rebuild();
  return wrap;
}

// ── MODULE EXPORTS ──────────────────────────────────────────────
/**
 * Capture a screenshot of a URL and return it as a vision-ready image object.
 * @param {string} url
 * @returns {Promise<import('./types.ts').UploadedImage | null>}
 */
async function captureInspoScreenshot(url) {
  if (!url) return null;
  try {
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 35_000);
    const resp = await fetch(`/api/screenshot?url=${encodeURIComponent(url)}`, { signal: ctrl.signal });
    clearTimeout(timeout);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const bitmap = await createImageBitmap(blob);
    let w = bitmap.width;
    let h = bitmap.height;
    if (w > 1568) {
      const scale = 1568 / w;
      h = Math.round(h * scale);
      w = 1568;
    }
    if (h > 3000) h = 3000;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    const dataUri = canvas.toDataURL('image/jpeg', 1);
    return {
      data: dataUri.replace(/^data:[^;]+;base64,/, ''),
      type: 'image/jpeg',
      name: 'inspiration.jpg',
      preview: dataUri,
      analysis: null,
      analysisStatus: 'done',
    };
  } catch {
    return null;
  }
}

/**
 * Create a reusable tab bar + content panel switcher.
 * Eliminates duplication between design and build screen tabs.
 * @param {{ barId: string, stateKey: string, tabs: Array<{id: string, label: string, contentId: string, render?: () => void}> }} config
 * @returns {{ renderTabs: () => void, renderContent: () => void }}
 */
function createTabSwitcher({ barId, stateKey, tabs }) {
  const renderTabs = () => {
    const c = $(barId);
    if (!c) return;
    c.innerHTML = '';
    tabs.forEach(tab => {
      c.append(mk('button', {
        class: `build-tab${ST[stateKey] === tab.id ? ' active' : ''}`,
        onclick() { ST[stateKey] = tab.id; renderTabs(); renderContent(); },
      }, tab.label));
    });
  };

  const renderContent = () => {
    tabs.forEach(tab => {
      const el = $(tab.contentId);
      if (el) el.style.display = ST[stateKey] === tab.id ? 'flex' : 'none';
    });
    const active = tabs.find(t => t.id === ST[stateKey]);
    active?.render?.();
  };

  return { renderTabs, renderContent };
}

export {
  $,
  assembled,
  assembledForPage,
  briefTxt,
  captureInspoScreenshot,
  createTabSwitcher,
  dv,
  esc,
  finalizeFrame,
  hide,
  loadFont,
  mk,
  mkLogoInput,
  mkUpload,
  patchFrame,
  REVEAL_FIX,
  refreshNotifyEmail,
  renderDevSw,
  renderLinkPreview,
  renderStepBar,
  show,
  showErr,
  showToast,
  sp,
  writeFrame,
};

window.$ = $;
window.show = show;
window.hide = hide;
